import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { CalendarClock, CheckCircle2, XCircle, UserX, ChevronRight } from 'lucide-react';

const Group = ({ title, rows, onOpen, color }) => (
  <div className="rounded-xl border border-[#E2E8F0] bg-white p-4">
    <div className="mb-3 flex items-center gap-2">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      <h3 className="text-sm font-bold text-[#172033]">{title}</h3>
      <span className="ml-auto text-xs font-semibold text-[#64748B]">{rows.length}</span>
    </div>
    {rows.length === 0 ? <p className="text-xs text-[#94A3B8]">None</p> : (
      <div className="space-y-1.5">
        {rows.map((c) => (
          <button key={c.case_id} data-testid={`test-row-${c.case_number}`} onClick={() => onOpen(c.case_id)}
            className="flex w-full items-center justify-between gap-2 rounded-lg border border-[#F1F5F9] px-3 py-2 text-left hover:bg-[#F8FAFC]">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[#172033]">{c.applicant_name}</p>
              <p className="text-xs text-[#64748B]">{c.class_name} {c.test?.date ? `· ${c.test.date} ${c.test.time || ''}` : ''} {c.test?.venue ? `· ${c.test.venue}` : ''}</p>
            </div>
            <ChevronRight size={15} className="text-[#CBD5E1]" />
          </button>
        ))}
      </div>
    )}
  </div>
);

export default function TestDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/dashboard/tests').then((r) => setData(r.data)); }, []);
  if (!data) return <p className="text-sm text-[#94A3B8]">Loading…</p>;
  const open = (id) => navigate(`/cases/${id}`);
  const counts = [
    { label: 'Passed', value: data.counts.pass, icon: CheckCircle2, color: '#16A34A' },
    { label: 'Failed', value: data.counts.fail, icon: XCircle, color: '#DC2626' },
    { label: 'Absent', value: data.counts.absent, icon: UserX, color: '#64748B' },
    { label: 'Scheduled', value: data.scheduled.length, icon: CalendarClock, color: '#7C3AED' },
  ];

  return (
    <div className="space-y-5" data-testid="test-dashboard-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#172033]">Admission Test Dashboard</h1>
        <p className="text-sm text-[#64748B]">Schedule, track and record admission test outcomes.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {counts.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="rounded-xl border border-[#E2E8F0] bg-white p-4">
              <Icon size={18} style={{ color: c.color }} />
              <p className="mt-2 font-heading text-2xl font-bold text-[#172033]">{c.value}</p>
              <p className="text-xs text-[#64748B]">{c.label}</p>
            </div>
          );
        })}
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Group title="Today's Tests" rows={data.today} onOpen={open} color="#DC2626" />
        <Group title="Upcoming Tests" rows={data.upcoming} onOpen={open} color="#7C3AED" />
        <Group title="Not Scheduled" rows={data.not_scheduled} onOpen={open} color="#EA580C" />
        <Group title="Scheduled" rows={data.scheduled} onOpen={open} color="#2563EB" />
        <Group title="Results Pending" rows={data.results_pending} onOpen={open} color="#D97706" />
        <Group title="Completed" rows={data.completed} onOpen={open} color="#0F766E" />
      </div>
    </div>
  );
}
